import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, ConfirmDialog, Section, useAuth, useToast } from "@ozpanel/ui";
import { DataList, type DataListColumn } from "./DataList";
import { RowInput } from "./Form";
import { StatusPill } from "./PageHeader";

// MailWebhooks — an account's delivery webhooks (mail/mail_webhooks.go): delivery outcomes pushed, signed,
// to an https endpoint. One component for the customer's Mail page and the operator's account page; the
// server decides who may touch which account. The operator also sets when a failing webhook is disabled.

type Hook = {
  id: number;
  url: string;
  states: string[];
  domain: string;
  enabled: boolean;
  consecutive_failures: number;
  last_error: string;
  disabled_reason: string;
  disabled_at: string | null;
  last_success_at: string | null;
  cursor_at: string;
  next_attempt_at: string;
};
type Policy = { disable_after_failures: number; disable_after_hours: number };
type Attempt = { id: number; at: string; batch_id: string; events: number; test: boolean; status: number; latency_ms: number; error: string; node: string };

const ALL_STATES = ["delivered", "deferred", "bounced", "rejected"];

const when = (s: string | null) => (s ? new Date(s).toLocaleString() : "never");

export function MailWebhooks({ tenantId, admin = false }: { tenantId: string | number; admin?: boolean }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const list = useQuery({
    queryKey: ["mail-webhooks", String(tenantId)],
    queryFn: () => client.apiFetch<{ items: Hook[] | null; policy: Policy }>(`/tenants/${tenantId}/mail/webhooks`),
  });
  const [url, setUrl] = React.useState("");
  const [domain, setDomain] = React.useState("");
  const [states, setStates] = React.useState<string[]>(ALL_STATES);
  const [secret, setSecret] = React.useState<string | null>(null);
  const [del, setDel] = React.useState<Hook | null>(null);
  const [logOf, setLogOf] = React.useState<number | null>(null);

  const [editing, setEditing] = React.useState<Hook | null>(null);

  const act = async (label: string, call: () => Promise<Record<string, unknown>>) => {
    try {
      const r = await call();
      await list.refetch();
      return r;
    } catch (e) {
      notify({ kind: "error", title: label, body: e instanceof Error ? e.message : String(e) });
      return null;
    }
  };

  const save = async () => {
    const body = JSON.stringify({ url: url.trim(), states, domain: domain.trim() });
    if (editing) {
      const r = await act("The webhook was not changed", () =>
        client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/mail/webhooks/${editing.id}`, { method: "PUT", body }),
      );
      if (r) stopEditing();
      return;
    }
    const r = await act("The webhook was not created", () =>
      client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/mail/webhooks`, { method: "POST", body }),
    );
    if (r && typeof r.secret === "string") {
      setSecret(r.secret);
      setUrl("");
      setDomain("");
    }
  };

  const edit = (h: Hook) => {
    setEditing(h);
    setUrl(h.url);
    setDomain(h.domain);
    setStates(h.states);
  };
  const stopEditing = () => {
    setEditing(null);
    setUrl("");
    setDomain("");
    setStates(ALL_STATES);
  };

  const test = async (h: Hook) => {
    const r = await act("The test was not sent", () =>
      client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/mail/webhooks/${h.id}/test`, { method: "POST" }),
    );
    if (!r) return;
    if (r.ok) notify({ kind: "success", title: `Test delivered (HTTP ${r.status}, ${r.latency_ms} ms)` });
    else notify({ kind: "error", title: "The endpoint did not accept the test", body: String(r.error ?? "") });
    if (logOf === h.id) void attempts.refetch();
  };

  const attempts = useQuery({
    queryKey: ["mail-webhook-attempts", String(tenantId), logOf],
    queryFn: () => client.apiFetch<{ items: Attempt[] | null }>(`/tenants/${tenantId}/mail/webhooks/${logOf}/attempts`),
    enabled: logOf != null,
  });

  const cols: DataListColumn<Hook>[] = [
    { key: "url", label: "Endpoint", render: (h) => <span className="break-all font-mono text-[12px]">{h.url}</span> },
    { key: "states", label: "Events", value: (h) => h.states.join(", ") + (h.domain ? ` · ${h.domain}` : "") },
    {
      key: "enabled",
      label: "State",
      value: (h) => (h.enabled ? "on" : "off"),
      render: (h) =>
        !h.enabled ? (
          <span title={h.disabled_reason}>
            <StatusPill tone="bad">disabled</StatusPill>
          </span>
        ) : h.consecutive_failures > 0 ? (
          <span title={h.last_error}>
            <StatusPill tone="warn">failing ×{h.consecutive_failures}</StatusPill>
          </span>
        ) : (
          <StatusPill tone="ok">sending</StatusPill>
        ),
    },
    { key: "last_success_at", label: "Last delivered", value: (h) => when(h.last_success_at) },
  ];

  const toggle = (s: string) => setStates((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const items = list.data?.items ?? [];
  const failing = items.filter((h) => !h.enabled && h.disabled_reason);

  return (
    <Section title="Delivery webhooks" hint="delivery outcomes pushed to your endpoint">
      <div className="space-y-3 p-4 text-[13px]">
        {failing.map((h) => (
          <div key={h.id} className="rounded-lg border border-line p-3 text-[12.5px]">
            <span className="text-bad">{h.url}</span> is disabled: {h.disabled_reason}. Fix the endpoint and enable it —
            it resumes from where it stopped.
          </div>
        ))}
        {secret && (
          <div className="rounded-lg border border-line p-3">
            <div className="text-[12px] text-muted">Signing secret — shown once. Store it now; verify every request with it.</div>
            <div className="mt-1 break-all font-mono text-[12.5px]">{secret}</div>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => setSecret(null)}>I have stored it</Button>
          </div>
        )}
        <DataList
          items={items}
          columns={cols}
          getRowKey={(h) => String(h.id)}
          loading={list.isLoading}
          error={list.error}
          errorContext="the delivery webhooks"
          emptyMessage="No webhooks. Add an https endpoint below to receive delivery events as they happen."
          rowActions={(h) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => void test(h)}>Send test</Button>
              <Button size="sm" variant="ghost" onClick={() => setLogOf(logOf === h.id ? null : h.id)}>Attempts</Button>
              <Button size="sm" variant="ghost" onClick={() => edit(h)}>Edit</Button>
              {h.enabled ? (
                <Button size="sm" variant="ghost" onClick={() => void act("Not paused", () => client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/mail/webhooks/${h.id}/disable`, { method: "POST" }))}>Pause</Button>
              ) : (
                <Button size="sm" variant="soft" onClick={() => void act("Not enabled", () => client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/mail/webhooks/${h.id}/enable`, { method: "POST" }))}>Enable</Button>
              )}
              <Button size="sm" variant="danger" onClick={() => setDel(h)}>Delete</Button>
            </div>
          )}
        />
        {logOf != null && (
          <div className="rounded-lg border border-line p-3">
            <div className="mb-2 text-[12px] text-muted">Recent attempts (newest first, last 100 kept)</div>
            {(attempts.data?.items ?? []).length === 0 && <div className="text-[12px] text-muted">No attempts yet.</div>}
            {(attempts.data?.items ?? []).map((a) => (
              <div key={a.id} className="flex flex-wrap gap-2 font-mono text-[11.5px]">
                <span>{when(a.at)}</span>
                <StatusPill tone={a.error ? "bad" : "ok"}>{a.status || "—"}</StatusPill>
                <span>{a.events} event(s){a.test ? " · test" : ""}</span>
                <span className="text-muted">{a.latency_ms} ms · {a.node}</span>
                {a.error && <span className="break-all text-bad">{a.error}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <RowInput value={url} placeholder="https://example.com/hooks/mail" onChange={setUrl} />
          <RowInput value={domain} placeholder="all domains" width="w-44" onChange={setDomain} />
          <Button size="sm" disabled={!url.trim().startsWith("https://") || states.length === 0} onClick={() => void save()}>
            {editing ? "Save changes" : "Add webhook"}
          </Button>
          {editing && <Button size="sm" variant="ghost" onClick={stopEditing}>Cancel</Button>}
        </div>
        <div className="flex flex-wrap gap-3 text-[12.5px]">
          {ALL_STATES.map((s) => (
            <label key={s} className="flex items-center gap-1">
              <input type="checkbox" checked={states.includes(s)} onChange={() => toggle(s)} /> {s}
            </label>
          ))}
        </div>
        <p className="text-[11.5px] leading-relaxed text-muted">
          Events arrive as a JSON array (up to 100 per request), a few minutes after the attempt. Each request carries{" "}
          <span className="font-mono">X-Ozpanel-Signature: sha256=&lt;HMAC-SHA256 of "timestamp.body" with your secret&gt;</span>,{" "}
          <span className="font-mono">X-Ozpanel-Timestamp</span> and <span className="font-mono">X-Ozpanel-Delivery</span>{" "}
          (the same on a retry). Answer 2xx within 10 s; anything else is retried from 30 s, backing off to an hour.
          After {list.data?.policy?.disable_after_failures ?? 10} failures in a row spanning{" "}
          {list.data?.policy?.disable_after_hours ?? 24} h the webhook is disabled and you are notified.
        </p>
        {admin && (
          <p className="text-[12.5px] text-muted">
            The disable threshold is platform-wide: Mail → Settings → Limits &amp; retention.
          </p>
        )}
      </div>
      <ConfirmDialog
        open={del != null}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete this webhook?"
        body={<span className="break-all">{del?.url} — its position in the event stream and its attempt log go with it.</span>}
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (del) await act("Not deleted", () => client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/mail/webhooks/${del.id}`, { method: "DELETE" }));
          setDel(null);
        }}
      />
    </Section>
  );
}

